import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useDragHandler } from '../../../utils/useDragHandler';
import * as THREE from 'three';
import { createLightSwich } from '../../../factory/symbols/createLightingSymbols';
import { setCommandType, setCommandMessage, setOperationData } from '../../../redux/operationSlice';
import { clearSelection } from '../../../redux/selectionSlice';
import { updateTesisat } from '../../../redux/tesisatSlice';
import { applySwitchHidden } from '../../../utils/switchHiddenState';
import { COMMANDS } from '../../../utils/commonProperties';
// eslint-disable-next-line no-restricted-imports -- CSS paylaşımlı, components/TypeMenus/'ta kaldı
import '../../../components/TypeMenus/PanelTypeMenus.css';

export default function LightSwitchTypeMenu({
  x,
  y,
  onClose,
  details = {},
  onUpdateDetails,
  scene,
  onDrag
}) {
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  const { handleMouseDown } = useDragHandler(onDrag);
  const [showSwitchSubmenu, setShowSwitchSubmenu] = useState(false);
  const [showFixtureSubmenu, setShowFixtureSubmenu] = useState(false);

  // Normal armatür türleri (etanj olmayan anahtarlar için)
  const normalFixtureTypes = [
    { value: 'normal', label: 'Normal' },
    { value: 'Kare Led Spot', label: 'Kare Led Spot' },
    { value: 'Asma Tavan Kare Floresans', label: 'Asma Tavan Kare Floresans' },
    { value: 'Yuvarlak Led Spot', label: 'Yuvarlak Led Spot' },
    { value: 'tablo', label: 'Tablo' },
    { value: 'aplik', label: 'Aplik' }
  ];

  // Etanj armatür türleri (etanj anahtarlar için)
  const etanjFixtureTypes = [
    { value: 'etanj', label: 'Etanj' }
  ];

  let switchObj;
  let switchUserData;

  if (scene && details.id) {
    scene.traverse(obj => {
      if (obj.userData.id === details.id) {
        switchObj = obj;
        switchUserData = obj.userData;
      }
    });
  }

  const [localDetails, setLocalDetails] = useState({
    switchType: switchUserData ? switchUserData.subtype : (details.switchType || 'adi'),
  });
  const [hidden, setHidden] = useState(!!switchUserData?.exportHidden);

  useEffect(() => {
    if (switchUserData) {
      setLocalDetails({
        switchType: switchUserData.subtype || 'adi',
      });
      setHidden(!!switchUserData.exportHidden);
    }
  }, [switchUserData]);

  // ✅ Anahtarı gizle/göster: editörde soluk (şeffaf) + PDF/DXF'e export edilmez.
  // Bazı aydınlatma hatlarında anahtar olmayabiliyor — "anahtar yok" görünümü.
  // Buatında SADECE bu tek anahtar varsa, buat da birlikte gizlenir/gösterilir.
  const LIGHTING_BUAT_TYPES = ['lightingBuat', 'buatCircle', 'buatLightingCircle', 'buatLightingSquare', 'buatSquare'];

  const findSwitchBuat = () => {
    const swPos = new THREE.Vector3();
    switchObj.getWorldPosition(swPos);
    let buat = null;
    scene.traverse(obj => {
      if (buat) return;
      if (!LIGHTING_BUAT_TYPES.includes(obj.userData?.type)) return;
      const bp = new THREE.Vector3();
      obj.getWorldPosition(bp);
      if (Math.hypot(bp.x - swPos.x, bp.y - swPos.y) < 1) buat = obj;
    });
    return buat;
  };

  const countSwitchesAtBuat = (buat) => {
    const bp = new THREE.Vector3();
    buat.getWorldPosition(bp);
    let count = 0;
    scene.traverse(obj => {
      const t = obj.userData?.type;
      if (t !== 'LightSwitch' && t !== 'lightSwitch') return;
      const sp = new THREE.Vector3();
      obj.getWorldPosition(sp);
      if (Math.hypot(sp.x - bp.x, sp.y - bp.y) < 1) count++;
    });
    return count;
  };

  const handleToggleHidden = () => {
    if (!switchObj) return;
    const next = !hidden;
    setHidden(next);
    applySwitchHidden(switchObj, next);
    dispatch(updateTesisat({ id: switchObj.userData.id, changes: { hidden: next } }));

    // Buatında sadece bu anahtar varsa buatı da gizle/göster
    const buat = findSwitchBuat();
    if (buat && countSwitchesAtBuat(buat) === 1) {
      applySwitchHidden(buat, next);
      dispatch(updateTesisat({ id: buat.userData.id, changes: { hidden: next } }));
    }

    dispatch(setCommandMessage(next ? 'Anahtar gizlendi (export edilmez)' : 'Anahtar gösteriliyor'));
  };

  const handleMouseDownWithStop = (e) => {
    e.stopPropagation();
    handleMouseDown(e, menuRef);
  };

  const handleTypeChange = (newType) => {
    setLocalDetails(prev => ({ ...prev, switchType: newType }));

    if (!switchObj || !scene) return;

    const oldSwitch = switchObj;
    const {
      id,
      basePoint,
      tempLineUUID,
      power,
      stretchLength
    } = oldSwitch.userData;

    // Mevcut TOPLAM rotasyonu al. offsetGroup.rotation.z = wireAngle + userRotate
    // (radyan). Yeni mesh dir=null ile yaratıldığında factory sadece currentRotate'i
    // uygular → toplam görsel açı korunur. userData.rotate sadece userRotate'i
    // tutar; tek başına kullanılırsa wire yönü kaybolur.
    let currentRotate;
    const oldOffsetGroup = oldSwitch.children?.find(c => c.userData?.isOffsetGroup === true);
    if (oldOffsetGroup) {
      currentRotate = (oldOffsetGroup.rotation.z * 180) / Math.PI;
    } else if (oldSwitch.userData.rotate !== undefined && oldSwitch.userData.rotate !== null) {
      currentRotate = oldSwitch.userData.rotate;
    } else {
      currentRotate = (oldSwitch.rotation.z * 180) / Math.PI;
    }

    // ✅ Bağlı wire'dan rengi al (öncelikli)
    let existingColor = 0x0045ff;
    let foundColor = false;

    // Önce bağlı wire'ı bul ve onun rengini al
    scene.traverse((obj) => {
      if (foundColor) return;
      if (obj.isLine &&
          obj.userData?.type === 'wireLighting' &&
          obj.userData?.lightSwitches?.includes(id)) {
        if (obj.material && obj.material.color) {
          existingColor = obj.material.color.getHex();
          foundColor = true;
        }
      }
    });

    // Wire bulunamazsa anahtar'ın kendi rengini kullan (fallback)
    if (!foundColor) {
      oldSwitch.traverse((child) => {
        if (foundColor) return;
        if (child.userData && (child.userData.role === 'LS_line1' || child.userData.role === 'LS_line2' || child.userData.role === 'LS_line3')) {
          if (child.material && child.material.color) {
            existingColor = child.material.color.getHex();
            foundColor = true;
          }
        }
      });
    }

    // Y kayma fix'inden sonra lightSwitch fpMesh-child olabilir; scene.remove()
    // sadece direkt scene-child'ları kaldırır → no-op. removeFromParent her
    // durumda ayırır (eski mesh sahnede üst üste kalmasın).
    oldSwitch.removeFromParent();
    oldSwitch.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });

    const newSwitch = createLightSwich(
      scene,
      new THREE.Vector3(basePoint.x, basePoint.y, 0),
      null,
      existingColor,
      false,
      newType,
      tempLineUUID,
      currentRotate,
      id,
      stretchLength
    );

    newSwitch.userData.id = id;
    newSwitch.userData.basePoint = basePoint;
    newSwitch.userData.tempLineUUID = tempLineUUID;
    newSwitch.userData.power = power;
    newSwitch.userData.rotate = currentRotate;
    newSwitch.userData.stretchLength = stretchLength;

    // ✅ Tip değişiminde gizli durumu koru (yeni mesh yaratıldı)
    if (hidden) applySwitchHidden(newSwitch, true);

    // ✅ createLightSwich zaten rotasyonu uyguluyor, tekrar uygulamaya gerek yok

    if (onUpdateDetails) {
      onUpdateDetails({ ...localDetails, switchType: newType });
    }
  };

  const handleAddSwitch = (switchType) => {
    if (!switchObj) return;

    // ✅ Anahtar'ın bağlı olduğu wire'ı bul
    let parentWire = null;
    scene.traverse(obj => {
      if (obj.isLine &&
          obj.userData?.type === 'wireLighting' &&
          obj.userData?.lightSwitches &&
          obj.userData.lightSwitches.includes(switchObj.userData?.id)) {
        parentWire = obj;
      }
    });

    // ✅ Anahtar pozisyonundaki buat'ı bul (world coords ile karşılaştır — sweep sonrası obj.position fpMesh-local olabilir)
    const switchWorldPos = new THREE.Vector3();
    switchObj.getWorldPosition(switchWorldPos);
    let buatObj = null;

    scene.traverse(obj => {
      if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') {
        const buatWorld = new THREE.Vector3();
        obj.getWorldPosition(buatWorld);
        const distance = Math.sqrt(
          Math.pow(buatWorld.x - switchWorldPos.x, 2) +
          Math.pow(buatWorld.y - switchWorldPos.y, 2)
        );
        // 1 birimden yakınsa bu buat'tır
        if (distance < 1) {
          buatObj = obj;
        }
      }
    });

    if (!buatObj) {
      console.warn("❌ Anahtar pozisyonunda buat bulunamadı");
      return;
    }

    // ✅ Source bilgisini hazırla
    const sourceData = {
      type: 'lightingBuat',
      panelID: parentWire?.userData?.panelID || buatObj.userData?.panelID,
      lightingLoadId: parentWire?.userData?.lightingLoadId || buatObj.userData?.lightingLoadId,
      wireID: parentWire?.userData?.id || buatObj.userData?.wireID,
      userData: {
        panelID: parentWire?.userData?.panelID || buatObj.userData?.panelID,
        lightingLoadId: parentWire?.userData?.lightingLoadId || buatObj.userData?.lightingLoadId,
        wireID: parentWire?.userData?.id || buatObj.userData?.wireID
      }
    };

    const buatWorldPos = new THREE.Vector3();
    buatObj.getWorldPosition(buatWorldPos);
    dispatch(clearSelection());
    dispatch(setCommandType(COMMANDS.DRAW_LIGHT));
    dispatch(setCommandMessage(`Buat'tan ${switchType} anahtar hattı başlatıldı. Sonraki noktaları seçin`));
    dispatch(setOperationData({
      autoSelectBuat: {
        buatId: buatObj.userData?.id,
        position: { x: buatWorldPos.x, y: buatWorldPos.y, z: buatWorldPos.z }
      },
      source: sourceData,
      defaultSwitchType: switchType // ✅ Varsayılan anahtar tipini ayarla
    }));
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="panel-type-menu"
      style={{ left: x, top: y }}
    >
      <div
        onMouseDown={handleMouseDownWithStop}
        className="panel-type-menu-header"
      >
        Anahtar Detay
        <button
          className="close-button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>
      </div>
      <div className="panel-type-options">
        <div className="panel-type-details-box">
          <table className="panel-type-details-table">
            <tbody>
              <tr>
                <td className="td">Tip</td>
                <td>
                  <select
                    style={{
                      width: "100%",
                      maxWidth: "160px",
                      backgroundColor: '#333',
                      color: '#eee',
                      border: '1px solid #555',
                      borderRadius: '4px',
                      padding: '2px'
                    }}
                    value={localDetails.switchType}
                    onChange={(e) => handleTypeChange(e.target.value)}
                  >
                    <option value="normal">Normal</option>
                    <option value="normal etanj">Normal Etanj</option>
                    <option value="Komütatör">Komütatör</option>
                    <option value="etanj Komütatör">Etanj Komütatör</option>
                    <option value="vaviyen">Vaviyen</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="panel-type-buttons">
            <button
                className="my-button"
                onClick={handleToggleHidden}
                style={{ width: '100%', textAlign: 'left' }}
                title="Gizli anahtar editörde soluk görünür, PDF/DXF'e export edilmez"
            >
                {hidden ? 'Anahtarı Göster' : 'Anahtarı Gizle'}
            </button>
            <div
                className="fixture-menu-container"
                style={{ position: 'relative', width: '100%' }}
                onMouseEnter={() => setShowFixtureSubmenu(true)}
                onMouseLeave={() => setShowFixtureSubmenu(false)}
            >
                <button
                    className="my-button"
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    Armatür Ekle <span style={{ fontSize: '16px', fontWeight: 'bold' }}>▸</span>
                </button>
                {showFixtureSubmenu && (
                    <div className="fixture-submenu" style={{
                        position: 'absolute',
                        left: '100%',
                        top: 0,
                        backgroundColor: '#2b2b2b',
                        border: '1px solid #444',
                        padding: '5px',
                        zIndex: 1000,
                        minWidth: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                    }}>
                        {/* ✅ Anahtar etanj ise etanj armatürler, değilse normal armatürler */}
                        {(localDetails.switchType?.toLowerCase().includes('etanj') ? etanjFixtureTypes : normalFixtureTypes).map((fixture) => (
                            <button
                                key={fixture.value}
                                onClick={() => {
                                    if (switchObj) {
                                        // ✅ Anahtar'ın bağlı olduğu wire'ı bul
                                        let parentWire = null;
                                        scene.traverse(obj => {
                                            if (obj.isLine &&
                                                obj.userData?.type === 'wireLighting' &&
                                                obj.userData?.lightSwitches &&
                                                obj.userData.lightSwitches.includes(switchObj.userData?.id)) {
                                                parentWire = obj;
                                            }
                                        });

                                        // ✅ Anahtar pozisyonundaki buat'ı bul (world coords — sweep sonrası position fpMesh-local olabilir)
                                        const swWorldPos = new THREE.Vector3();
                                        switchObj.getWorldPosition(swWorldPos);
                                        let buatObj = null;

                                        scene.traverse(obj => {
                                            if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') {
                                                const bWorld = new THREE.Vector3();
                                                obj.getWorldPosition(bWorld);
                                                const distance = Math.sqrt(
                                                    Math.pow(bWorld.x - swWorldPos.x, 2) +
                                                    Math.pow(bWorld.y - swWorldPos.y, 2)
                                                );
                                                // 1 birimden yakınsa bu buat'tır
                                                if (distance < 1) {
                                                    buatObj = obj;
                                                }
                                            }
                                        });

                                        if (!buatObj) {
                                            console.warn("❌ Anahtar pozisyonunda buat bulunamadı");
                                            return;
                                        }

                                        // ✅ Source bilgisini hazırla
                                        const sourceData = {
                                            type: 'lightingBuat',
                                            panelID: parentWire?.userData?.panelID || buatObj.userData?.panelID,
                                            switchLoadId: parentWire?.userData?.switchLoadId || buatObj.userData?.switchLoadId,
                                            wireID: parentWire?.userData?.id || buatObj.userData?.wireID,
                                            lightingLoadId: parentWire?.userData?.lightingLoadId || buatObj.userData?.lightingLoadId,
                                            userData: {
                                                panelID: parentWire?.userData?.panelID || buatObj.userData?.panelID,
                                                switchLoadId: parentWire?.userData?.switchLoadId || buatObj.userData?.switchLoadId,
                                                wireID: parentWire?.userData?.id || buatObj.userData?.wireID,
                                                lightingLoadId: parentWire?.userData?.lightingLoadId || buatObj.userData?.lightingLoadId
                                            }
                                        };

                                        const bwp = new THREE.Vector3();
                                        buatObj.getWorldPosition(bwp);
                                        dispatch(clearSelection());
                                        dispatch(setCommandType(COMMANDS.DRAW_FIXTURE));
                                        dispatch(setCommandMessage(`Buat'tan ${fixture.label} armatür hattı başlatıldı. Sonraki noktaları seçin`));
                                        dispatch(setOperationData({
                                            autoSelectBuat: {
                                                buatId: buatObj.userData?.id,
                                                position: { x: bwp.x, y: bwp.y, z: bwp.z }
                                            },
                                            source: sourceData,
                                            defaultFixtureType: fixture.value // ✅ Varsayılan armatür tipini ayarla
                                        }));
                                        onClose();
                                    }
                                }}
                                className="my-button"
                                style={{ textAlign: 'left', fontSize: '12px' }}
                            >
                                {fixture.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div
                className="switch-menu-container"
                style={{ position: 'relative', width: '100%' }}
                onMouseEnter={() => setShowSwitchSubmenu(true)}
                onMouseLeave={() => setShowSwitchSubmenu(false)}
            >
              <button
                className="my-button"
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                Anahtar Ekle <span style={{ fontSize: '16px', fontWeight: 'bold' }}>▸</span>
              </button>

              {showSwitchSubmenu && (
                <div style={{
                  position: 'absolute',
                  left: '100%',
                  top: '0',
                  backgroundColor: '#2b2b2b',
                  border: '1px solid #444',
                  padding: '5px',
                  minWidth: '150px',
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <button
                    className="my-button"
                    onClick={() => handleAddSwitch('normal')}
                    style={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    Anahtar
                  </button>
                  <button
                    className="my-button"
                    onClick={() => handleAddSwitch('Komütatör')}
                    style={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    Komütatör
                  </button>
                  <button
                    className="my-button"
                    onClick={() => handleAddSwitch('normal etanj')}
                    style={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    Etanj
                  </button>
                  <button
                    className="my-button"
                    onClick={() => handleAddSwitch('etanj Komütatör')}
                    style={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    Etanj Komütatör
                  </button>
                  <button
                    className="my-button"
                    onClick={() => handleAddSwitch('vaviyen')}
                    style={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    Vaviyen
                  </button>
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
}
